import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="max-w-4xl mx-auto px-6 py-10 text-gray-800">
      <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-6">Last updated: September 20, 2025</p>

      <p className="mb-4">
        This Privacy Policy describes how <strong>Aaladipattiyan Private Limited</strong> (“Company”,
        “we”, “our”, or “us”) collects, uses, and discloses your information when you use
        <strong> Aaladipattiyan Customer Rewards</strong> (“Service”), and explains your privacy rights
        and how applicable laws protect you.
      </p>
      <p className="mb-6">
        By using our Service, you agree to the collection and use of information in accordance with
        this Privacy Policy.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-4">1. Interpretation & Definitions</h2>
      <p className="mb-2 font-semibold pl-6">Interpretation</p>
      <p className="mb-4 pl-8">
        Words with capitalized initial letters have meanings defined under the following conditions.
        The definitions below apply whether they appear in singular or plural.
      </p>

      <p className="mb-2 font-semibold pl-6">Definitions</p>
      <ul className="list-disc list-inside space-y-2 pl-8 mb-6">
        <li><strong>Account:</strong> A unique account created for you to access our Service or parts of our Service.</li>
        <li><strong>Affiliate:</strong> An entity that controls, is controlled by, or is under common control with us.</li>
        <li><strong>Company:</strong> Refers to Aaladipattiyan Private Limited, 7/315–7/319, SDM to Anaikulam Road, Pandiyapuram, Tenkasi, Tamil Nadu, India – 627857.</li>
        <li><strong>Cookies:</strong> Small files placed on your device that store browsing history and other usage details.</li>
        <li><strong>Country:</strong> Tamil Nadu, India.</li>
        <li><strong>Device:</strong> Any device that can access the Service (e.g., computer, smartphone, tablet).</li>
        <li><strong>Personal Data:</strong> Any information that relates to an identified or identifiable individual.</li>
        <li><strong>Service:</strong> The Aaladipattiyan Customer Rewards website, available at <a href="https://rewards.aaladipattiyan.in" className="text-blue-600 underline">https://rewards.aaladipattiyan.in</a>.</li>
        <li><strong>Service Provider:</strong> Third parties that process data on our behalf.</li>
        <li><strong>Usage Data:</strong> Information collected automatically, such as IP address, browser type, device identifiers, and activity on the Service.</li>
        <li><strong>You:</strong> The individual using the Service, or the company/legal entity they represent.</li>
      </ul>

      <h2 className="text-xl font-semibold mt-8 mb-4">2. Data We Collect</h2>
      <p className="mb-2 font-semibold pl-6">Personal Data</p>
      <ul className="list-disc list-inside space-y-2 pl-8  mb-4">
        <li>Name (if provided)</li>
        <li>Email address</li>
        <li>Phone number</li>
        <li>Usage Data</li>
      </ul>

      <p className="mb-2 font-semibold pl-6">Usage Data</p>
      <ul className="list-disc list-inside space-y-2 pl-8  mb-6">
        <li>IP address, browser type/version</li>
        <li>Device type, operating system, mobile network info</li>
        <li>Pages visited, time/date of visits, session duration</li>
        <li>Diagnostic data</li>
      </ul>

      <h2 className="text-xl font-semibold mt-8 mb-4">3. Tracking Technologies & Cookies</h2>
      <p className="mb-4 pl-6">We use cookies, beacons, and similar technologies to:</p>
      <ul className="list-disc list-inside space-y-2 pl-8 mb-4">
        <li>Authenticate users</li>
        <li>Remember preferences (e.g., login details, language)</li>
        <li>Improve functionality and measure usage</li>
      </ul>
      <p className="mb-2 font-semibold pl-6">Types of cookies we use:</p>
      <ul className="list-disc list-inside space-y-2 pl-8 mb-6">
        <li>Essential Cookies</li>
        <li>Acceptance Cookies</li>
        <li>Functionality Cookies</li>
      </ul>

      <h2 className="text-xl font-semibold mt-8 mb-4 ">4. Use of Personal Data</h2>
      <ul className="list-disc list-inside space-y-2 pl-6 mb-6">
        <li>Provide and maintain the Service</li>
        <li>Manage your Account and access</li>
        <li>Fulfill contracts or transactions</li>
        <li>Communicate with you (email, SMS, push notifications)</li>
        <li>Send updates, offers, and promotions (unless you opt out)</li>
        <li>Analyze usage trends and improve services</li>
        <li>Support legal, compliance, and security obligations</li>
        <li>Facilitate business transfers (merger, sale, acquisition)</li>
      </ul>

      <h2 className="text-xl font-semibold mt-8 mb-4">5. Sharing of Personal Data</h2>
      <ul className="list-disc list-inside space-y-2 pl-6 mb-6">
        <li>Service Providers</li>
        <li>Affiliates</li>
        <li>Business Partners</li>
        <li>Other Users</li>
        <li>Legal Authorities</li>
        <li>Third Parties (with explicit consent)</li>
      </ul>

      <h2 className="text-xl font-semibold mt-8 mb-4">6. Data Retention</h2>
      <p className="mb-6 pl-6">
        Personal Data is retained only as long as necessary. Usage Data may be retained longer for
        security, analytics, or compliance.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-4">7. Data Transfers</h2>
      <p className="mb-6 pl-6">
        Your information may be processed outside your state/country. We ensure transfers comply
        with data protection laws and maintain safeguards.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-4">8. Your Rights</h2>
      <ul className="list-disc list-inside space-y-2 pl-6 mb-6">
        <li>Access, update, or delete your data</li>
        <li>Withdraw consent where applicable</li>
        <li>Request deletion of Personal Data (subject to legal obligations)</li>
      </ul>

      <h2 className="text-xl font-semibold mt-8 mb-4">9. Children’s Privacy</h2>
      <p className="mb-6 pl-6">
        Our Service is not intended for children under 13. If you believe a child has shared data,
        please contact us for removal.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-4">10. Security of Data</h2>
      <p className="mb-6 pl-6">
        We take reasonable technical and organizational measures to protect your Personal Data.
        However, no method of online transmission or storage is 100% secure.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-4">11. Third-Party Links</h2>
      <p className="mb-6 pl-6">
        Our Service may contain links to third-party websites. We are not responsible for their
        privacy practices or content.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-4">12. Changes to This Privacy Policy</h2>
      <p className="mb-6 pl-6">
        We may update this Policy from time to time. Updates will be posted on this page with a
        revised “Last updated” date.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-4">13. Contact Us</h2>
      <p className="mb-2 pl-6">For questions or requests regarding this Privacy Policy, please contact us:</p>
      <ul className="list-disc list-inside space-y-2 pl-10">
        <li>Email: <a href="mailto:support@halwakadai.com" className="text-blue-600 underline">support@halwakadai.com</a></li>
        <li>Phone: +91 93614 61549</li>
      </ul>
    </div>
  );
};

export default PrivacyPolicy;
